/* eslint-disable @typescript-eslint/no-explicit-any */
import { CPR, CprEntry, TagAttribution, RouteFamily, normalizeRouteFamily } from '../BgpVisibilityData';
import { normalizeTagAttribution } from '../helpers/bgpVisibilityApi';

/**
 * A single normalized route-row: one (prefix, route_table/subnet, neighbor) association,
 * covering both the primary decision and each alternate candidate path as its own row.
 * Topology, filters, table, and metrics all derive from this shared model instead of
 * re-scanning the raw CPR entries independently.
 */
export interface RouteRow {
  id: string;
  prefix: string;
  direction: 'inbound' | 'outbound';
  family: RouteFamily;
  neighborIp: string;
  neighborAs: number | null;
  reflectorId: string;
  reflectorName: string;
  /** Announcing member's HA role, so cluster views can say *which* member without a second lookup. */
  reflectorRole: 'PRIMARY' | 'SECONDARY' | null;
  clusterId: string | null;
  clusterName: string | null;
  isBestPath: boolean;
  localPref: number;
  med: number;
  asPath: number[];
  nextHop: string | null;
  vpc: string;
  routeTableId: string;
  subnetId: string | null;
  rd: string | null;
  rt: string | null;
  status: string | null;
  timestamp: number;
  withdrawn: boolean;
  withdrawnNeighborIp: string | null;
  /** Epoch seconds on the wire (naboo sends a float); older payloads may carry an ISO string. */
  receivedAt: string | number | null;
  origin: string;
  decisionReason: string;
  /**
   * Which tag(s) put this row's destination (route table inbound / subnet outbound) in the
   * rule's scope, and how it was selected; `null` when the payload carries no attribution
   * (CTX-129). Outbound can carry several tags, inbound exactly one.
   */
  tagAttribution: TagAttribution | null;
  /**
   * Cloud outcome for this row's (prefix, route table): what the reflector
   * wrote (`injection*`) and what the reconciliation sweep last saw in AWS
   * (`aws*`). All null on reflectors that predate the feature — never read
   * them directly, go through `deriveCloudRouteHealth` (CTX-323/CTX-328).
   */
  injectionStatus: 'injected' | 'failed' | 'withdrawn' | null;
  injectionError: string | null;
  injectionAt: number | null;
  awsState: 'active' | 'blackhole' | 'missing' | 'drift' | 'table_missing' | null;
  awsCheckedAt: number | null;
}

export interface BuildRouteRowsParams {
  cpr: CPR | null;
  direction: 'INBOUND' | 'OUTBOUND';
  reflectorId: string;
  reflectorName: string;
  /** Announcing member's HA role; null outside cluster mode or when the backend reports none. */
  reflectorRole?: 'PRIMARY' | 'SECONDARY' | null;
  clusterId: string | null;
  /** Cluster display name for the detail view; falls back to clusterId when absent. */
  clusterName?: string | null;
  /** peer IP -> ASN, sourced from the neighbor-data endpoint */
  neighborAsByIp?: Record<string, number | undefined>;
}

/**
 * Builds the normalized route-row list in a single pass over the CPR entries
 * (plus one inner pass over each entry's candidates), avoiding the repeated
 * `rows.filter(...)` nested scans the previous per-neighbor/per-prefix grouping used.
 * Every downstream projection (topology, table, metrics) derives from this list.
 */
export function buildRouteRows({
  cpr,
  direction,
  reflectorId,
  reflectorName,
  reflectorRole = null,
  clusterId,
  clusterName = null,
  neighborAsByIp = {}
}: BuildRouteRowsParams): RouteRow[] {
  const rows: RouteRow[] = [];

  if (!cpr) {
    return rows;
  }

  const apiDirection: 'inbound' | 'outbound' = direction === 'INBOUND' ? 'inbound' : 'outbound';
  const set = direction === 'INBOUND' ? cpr.INBOUND : cpr.OUTBOUND;
  const entries: CprEntry[] = [
    ...((set as any).ipv4_unicast_cpr || []),
    ...(direction === 'INBOUND' ? ((set as any).vpnv4_cpr || []) : [])
  ];

  const pushRow = (row: RouteRow) => {
    rows.push(row);
  };

  for (const entry of entries) {
    const family = normalizeRouteFamily(entry.family);
    // Row id includes reflectorId so cluster all-member views never collapse two
    // reflectors' identical (prefix, route-table, neighbor) rows into one (AC requirement).
    const baseId = `${reflectorId}-${apiDirection}-${family}-${entry.prefix}-${entry.route_table_id}`;
    // A route is withdrawn when the transform flagged it, or (defensively, for flat entries that
    // reach the model without going through the transform) when the wire action is 'delete' or
    // the decision reason is an explicit withdraw.
    const withdrawn =
      entry.withdrawn === true ||
      entry.action === 'delete' ||
      entry.decision_reason === 'delete_explicit_withdraw';
    const status = entry.status || (withdrawn ? 'withdrawn' : null);
    // Cloud outcome belongs to the (prefix, route table) pair, so the primary
    // row and every alternate path of this entry share it.
    const cloud = {
      injectionStatus: entry.injection_status ?? null,
      injectionError: entry.injection_error ?? null,
      injectionAt: entry.injection_at ?? null,
      awsState: entry.aws_state ?? null,
      awsCheckedAt: entry.aws_checked_at ?? null
    };

    pushRow({
      id: `${baseId}-${entry.neighbor_ip}`,
      prefix: entry.prefix,
      direction: apiDirection,
      family,
      neighborIp: entry.neighbor_ip,
      neighborAs: neighborAsByIp[entry.neighbor_ip] ?? null,
      reflectorId,
      reflectorName,
      reflectorRole,
      clusterId,
      clusterName,
      isBestPath: entry.selected === true,
      localPref: entry.local_pref,
      med: entry.med,
      asPath: entry.as_path || [],
      nextHop: entry.specific_next_hop || null,
      vpc: entry.vpc,
      routeTableId: entry.route_table_id,
      subnetId: entry.subnet_id || null,
      rd: entry.rd,
      rt: entry.rt,
      status,
      timestamp: entry.timestamp,
      withdrawn,
      withdrawnNeighborIp: entry.withdrawn_neighbor_ip,
      receivedAt: entry.received_at,
      origin: entry.origin,
      decisionReason: entry.decision_reason,
      tagAttribution: entry.tag_attribution ?? null,
      ...cloud
    });

    for (const candidate of entry.candidates || []) {
      if (candidate.neighbor_ip === entry.neighbor_ip) {
        continue;
      }
      pushRow({
        id: `${baseId}-${candidate.neighbor_ip}`,
        prefix: entry.prefix,
        direction: apiDirection,
        family,
        neighborIp: candidate.neighbor_ip,
        neighborAs: neighborAsByIp[candidate.neighbor_ip] ?? null,
        reflectorId,
        reflectorName,
        reflectorRole,
        clusterId,
        clusterName,
        isBestPath: candidate.selected === true,
        localPref: candidate.local_pref,
        med: candidate.med,
        asPath: candidate.as_path || [],
        nextHop: entry.specific_next_hop || null,
        vpc: entry.vpc,
        routeTableId: entry.route_table_id,
        subnetId: entry.subnet_id || null,
        rd: entry.rd,
        rt: entry.rt,
        status,
        timestamp: entry.timestamp,
        withdrawn,
        withdrawnNeighborIp: entry.withdrawn_neighbor_ip,
        receivedAt: entry.received_at,
        origin: candidate.origin,
        // Alternate paths reach the same destination, so they inherit its provenance.
        decisionReason: entry.decision_reason,
        tagAttribution: entry.tag_attribution ?? null,
        ...cloud
      });
    }
  }

  return rows;
}

/**
 * One route row exactly as `GET /v2/reflector/visibility/routes` serves it: the
 * backend's own normalization of the same S3 documents `buildRouteRows` reads,
 * flattened to one row per (prefix, route-table/subnet, neighbor) and snake_cased.
 *
 * Deliberately narrower than the endpoint's payload — only the fields the table
 * actually renders are modelled, so a field the UI never shows cannot quietly
 * become a dependency.
 */
export interface WireRouteRow {
  id: string;
  prefix: string;
  direction: 'inbound' | 'outbound';
  family: string;
  neighbor_ip: string | null;
  neighbor_as: number | null;
  reflector_id: string;
  reflector_name: string;
  cluster_id: string | number | null;
  selected: boolean;
  local_pref: number | null;
  med: number | null;
  as_path: number[] | null;
  next_hop: string | null;
  vpc: string | null;
  route_table_id: string | null;
  subnet_id: string | null;
  rd: string | null;
  rt: string | null;
  status: string | null;
  timestamp: number | null;
  withdrawn: boolean;
  withdrawn_neighbor_ip: string | null;
  received_at: string | number | null;
  origin: string | null;
  decision_reason: string | null;
  tag_attribution: unknown;
  injection_status: RouteRow['injectionStatus'];
  injection_error: string | null;
  injection_at: number | null;
  aws_state: RouteRow['awsState'];
  aws_checked_at: number | null;
}

const numberOr = (value: number | null | undefined, fallback: number): number =>
  typeof value === 'number' && !Number.isNaN(value) ? value : fallback;

/**
 * Maps one wire row onto the shared `RouteRow` model, so table, filters and metrics
 * keep reading a single shape regardless of which side flattened the document.
 *
 * The wire is trusted for provenance and cloud state but not for absence: every
 * optional field lands on the same "absent" value `buildRouteRows` would produce, so
 * a row served by an older backend degrades to blank rather than to `undefined`
 * reaching a renderer.
 *
 * Two fields the endpoint has no column for are left null here and attached by the
 * caller, which holds the cluster member list: the announcing member's HA role (the
 * PRIMARY / SECONDARY badge) and the cluster's display name.
 */
export function mapWireRouteRow(wire: WireRouteRow): RouteRow {
  return {
    id: wire.id,
    prefix: wire.prefix,
    direction: wire.direction,
    family: normalizeRouteFamily(wire.family),
    // A withdraw tombstone has no announcing peer; the backend keys the row by the
    // withdrawing one, so `neighbor_ip` is only ever null on a malformed row.
    neighborIp: wire.neighbor_ip ?? '',
    neighborAs: wire.neighbor_as ?? null,
    reflectorId: wire.reflector_id,
    reflectorName: wire.reflector_name,
    reflectorRole: null,
    clusterId: wire.cluster_id != null ? String(wire.cluster_id) : null,
    clusterName: null,
    isBestPath: wire.selected === true,
    localPref: numberOr(wire.local_pref, 0),
    med: numberOr(wire.med, 0),
    asPath: Array.isArray(wire.as_path) ? wire.as_path : [],
    nextHop: wire.next_hop ?? null,
    vpc: wire.vpc ?? '',
    routeTableId: wire.route_table_id ?? '',
    subnetId: wire.subnet_id ?? null,
    rd: wire.rd ?? null,
    rt: wire.rt ?? null,
    status: wire.status ?? null,
    timestamp: numberOr(wire.timestamp, 0),
    withdrawn: wire.withdrawn === true,
    withdrawnNeighborIp: wire.withdrawn_neighbor_ip ?? null,
    receivedAt: wire.received_at ?? null,
    origin: wire.origin ?? '',
    decisionReason: wire.decision_reason ?? '',
    // Normalized again on this side on purpose: it is idempotent for the shape the
    // backend emits, and it is what keeps a backend that predates the projection
    // (CTX-129 / CTX-582) rendering as untagged instead of as a malformed chip.
    tagAttribution: normalizeTagAttribution(wire.tag_attribution),
    injectionStatus: wire.injection_status ?? null,
    injectionError: wire.injection_error ?? null,
    injectionAt: wire.injection_at ?? null,
    awsState: wire.aws_state ?? null,
    awsCheckedAt: wire.aws_checked_at ?? null
  };
}

export function mapWireRouteRows(wireRows: WireRouteRow[]): RouteRow[] {
  return wireRows.map(mapWireRouteRow);
}

export interface RouteRowGroup {
  prefix: string;
  /** Every underlying route sharing this prefix, in the order encountered — never merged. */
  rows: RouteRow[];
  /** Row used for the group's single-value columns (best path if present, else the first row). */
  representative: RouteRow;
}

/**
 * Groups an (already filtered) row list by exact prefix string (scope C.1) so the table can
 * display each prefix once with a multiplicity indicator. Grouping is purely a display
 * concern — every underlying row is preserved in `rows` (including ones that differ by
 * reflector, neighbor, family, direction, or destination) so expanding a group never loses
 * or silently merges provenance.
 */
export function groupRoutesByPrefix(rows: RouteRow[]): RouteRowGroup[] {
  const groups = new Map<string, RouteRow[]>();
  for (const row of rows) {
    const existing = groups.get(row.prefix);
    if (existing) {
      existing.push(row);
    } else {
      groups.set(row.prefix, [row]);
    }
  }
  return Array.from(groups.entries()).map(([prefix, groupRows]) => ({
    prefix,
    rows: groupRows,
    representative: groupRows.find(r => r.isBestPath) || groupRows[0]
  }));
}

/**
 * The minimum shape needed to identify a prefix. Deliberately structural rather than
 * `RouteRow`: the cloud-side surfaces (VPC card, route-table modal) receive wire-shaped
 * entries projected out of the graph, not rows, and must count them the same way.
 */
export interface PrefixIdentity {
  prefix: string;
  family: string;
}

/**
 * How a prefix is identified wherever the UI reports "N prefixes": the CIDR plus its address
 * family. The normalized model carries one row per candidate path, so counting rows reports
 * *paths* — which is how the VPC card, the route-table chip and the route-table modal came to
 * say `32` on a screen whose toolbar said `16` (CTX-322). Every surface counts through here so
 * they cannot drift apart again.
 */
export const prefixIdentityKey = ({ prefix, family }: PrefixIdentity): string => `${prefix}|${family}`;

/** Distinct prefixes across rows or wire entries — the figure reported as "N prefixes". */
export const countDistinctPrefixes = (entries: PrefixIdentity[]): number =>
  new Set(entries.map(prefixIdentityKey)).size;

export interface RouteMetrics {
  neighbors: number;
  vpcs: number;
  routeTables: number;
  prefixes: number;
  withdrawn: number;
}

/** Single-pass metrics over an (already filtered) row list. */
export function computeRouteMetrics(rows: RouteRow[]): RouteMetrics {
  const neighbors = new Set<string>();
  const vpcs = new Set<string>();
  const routeTables = new Set<string>();
  const prefixes = new Set<string>();
  // Count distinct withdrawn prefixes, not rows — a withdrawn prefix expands into one row
  // per candidate path, and "N withdrawn prefixes" is the meaningful figure.
  const withdrawnPrefixes = new Set<string>();

  for (const row of rows) {
    neighbors.add(row.neighborIp);
    vpcs.add(row.vpc);
    routeTables.add(row.routeTableId);
    const prefixKey = prefixIdentityKey(row);
    prefixes.add(prefixKey);
    if (row.withdrawn) {
      withdrawnPrefixes.add(prefixKey);
    }
  }

  return {
    neighbors: neighbors.size,
    vpcs: vpcs.size,
    routeTables: routeTables.size,
    prefixes: prefixes.size,
    withdrawn: withdrawnPrefixes.size
  };
}
